import { useEffect, useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { API } from "@/lib/api";
import { MessageCircle, X, Send } from "lucide-react";

export default function ChatWidget() {
  const { locale, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())));
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t.chat.welcome }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, history, locale, session_id: sessionId }),
      });
      if (!res.ok || !res.body) throw new Error("chat_failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = part.slice(6).trim();
          if (data === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(data);
            if (delta) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
                return copy;
              });
            }
          } catch {
            /* partial chunk */
          }
        }
      }
    } catch {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", content: t.chat.error };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50" data-testid="chat-widget">
      {open && (
        <div
          className="mb-3 w-[360px] max-w-[calc(100vw-2.5rem)] h-[480px] max-h-[70vh] rounded-lg border border-[#E2C079]/30 bg-[#0f1b29] shadow-2xl flex flex-col overflow-hidden"
          data-testid="chat-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#0b131e]">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="" className="h-7 w-auto" />
              <div className="leading-tight">
                <p className="text-xs font-semibold text-white font-mono-code uppercase tracking-wider">{t.chat.title}</p>
                <p className="text-[10px] text-white/50">{t.chat.subtitle}</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label={t.chat.closeLabel} data-testid="chat-close-button" className="text-white/60 hover:text-white">
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-[#E2C079] text-[#0b131e]" : "bg-white/5 border border-white/10 text-white/90"
                  }`}
                  data-testid={`chat-message-${m.role}-${i}`}
                >
                  {m.content}
                  {m.role === "assistant" && streaming && i === messages.length - 1 && (
                    <span className="inline-block w-1.5 h-3 ml-1 bg-[#E2C079] animate-pulse"></span>
                  )}
                </div>
              </div>
            ))}
            <div ref={bottomRef}></div>
          </div>

          <form onSubmit={send} className="flex items-center gap-2 p-3 border-t border-white/10 bg-[#0b131e]">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t.chat.placeholder}
              aria-label={t.chat.placeholder}
              data-testid="chat-input"
              className="flex-1 bg-[#0f1b29] border border-white/20 rounded px-3 py-2 text-xs text-white focus:border-[#E2C079] outline-none"
            />
            <button
              type="submit"
              disabled={streaming || !input.trim()}
              aria-label={t.chat.send}
              data-testid="chat-send-button"
              className="p-2 rounded bg-[#E2C079] text-[#0b131e] hover:bg-white transition-colors disabled:opacity-50"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? t.chat.closeLabel : t.chat.openLabel}
        data-testid="chat-toggle-button"
        className="ml-auto flex w-14 h-14 rounded-full bg-[#E2C079] text-[#0b131e] items-center justify-center shadow-lg shadow-[#E2C079]/20 hover:bg-white transition-colors"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
    </div>
  );
}
